/* eslint-disable no-console */
import { clearProxyAuth } from '@authentication/clearProxyAuth';
import { TransitionScreen } from '@components/transition';
import { WindowWithRegisterNetworkRecovery } from '@hooks/NetworkStatusProvider';
import useKeycloak from '@hooks/UseKeycloak';
import { getThemeMode } from '@theme/Theme';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import posthog from 'posthog-js';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type AuthContextType = {
  loading: boolean;
  error?: Error;
  user?: KeycloakUser;
  authenticated: boolean;
  token?: string;
  logout: (redirectUri?: string) => void;
  register: () => void;
};

type KeycloakUser = Keycloak['tokenParsed'] & {
  preferred_username: string;
  email: string;
  name: string;
  uid: string;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<KeycloakUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error>();
  const [authenticated, setAuthenticated] = useState(false);
  const [authClient, setAuthClient] = useState<Keycloak>();

  const { keycloak, loading: keycloakLoading } = useKeycloak();

  const logout = useMemo(() => {
    return (redirectUri?: string) => {
      setLoading(true);
      posthog.capture('user_logout_intent');
      setUser(null);
      void authClient?.logout({ redirectUri });
    };
  }, [authClient]);

  const register = useMemo(() => {
    return () => {
      setLoading(true);
      posthog.capture('user_registration_intent');
      void authClient?.register({
        redirectUri:
          typeof window !== 'undefined'
            ? `${window.location.origin}/register?registration_complete=true`
            : undefined,
      });
    };
  }, [authClient]);

  const refreshToken = useCallback(
    async (force = false) => {
      try {
        if (!authClient) return false;

        // Refresh if token expires within 1 hour, or immediately for network recovery
        const minValiditySeconds = force ? 0 : 60 * 60;
        const refreshed = await authClient.updateToken(minValiditySeconds);
        if (refreshed) {
          setUser(authClient.tokenParsed as KeycloakUser);
          setAuthenticated(true);
          return true;
        }
        return false;
      } catch (err) {
        console.error('Failed to refresh token:', err);
        setError(err instanceof Error ? err : new Error('Failed to refresh token'));
        setAuthenticated(false);
        logout();
        return false;
      }
    },
    [authClient, logout],
  );

  // Network recovery functionality
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (window as WindowWithRegisterNetworkRecovery).__registerNetworkRecovery &&
      authClient
    ) {
      const unregister = (window as WindowWithRegisterNetworkRecovery).__registerNetworkRecovery(
        async () => {
          // Force a token refresh when network comes back
          if (authenticated && authClient) {
            try {
              const success = await refreshToken(true);
              if (!success) {
                console.warn('Auth: Failed to refresh token during network recovery');
              }
            } catch (err) {
              console.error('Auth: Error during network recovery token refresh:', err);
            }
          }
        },
      );

      return unregister;
    }
    return () => {};
  }, [authClient, authenticated, refreshToken]);

  useEffect(() => {
    if (!keycloak || keycloakLoading) {
      return;
    }

    if (keycloak.sessionId) {
      setLoading(false);
      setAuthenticated(keycloak.authenticated);
      setAuthClient(keycloak);
      setUser(keycloak.tokenParsed as KeycloakUser);
      return;
    }

    keycloak.onReady = () => {
      // If silent SSO check found no existing session, redirect to login
      if (!keycloak.authenticated && !isRegisterPage && !isRegisterAsPage) {
        void keycloak.login();
        return;
      }
      setLoading(false);
    };
    keycloak.onAuthSuccess = () => {
      // Clear any stale proxy cookie from a previous user session
      void clearProxyAuth();
      posthog.capture('user_login_complete');
      posthog.capture('user_login');
      const { email, uid } = keycloak.tokenParsed as KeycloakUser;
      posthog.identify(email, { email });
      posthog.alias(uid, email);
      setAuthenticated(true);
      setLoading(false);
      setUser(keycloak.tokenParsed as KeycloakUser);
    };
    keycloak.onAuthError = (errorData) => {
      setAuthenticated(false);
      posthog.capture('user_login_failed', { error: errorData });
      setLoading(false);
      setUser(null);
    };
    keycloak.onAuthLogout = () => {
      posthog.capture('user_logout_complete');
      posthog.reset();
      setAuthenticated(false);
      setLoading(false);
      setUser(null);
    };
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(5).catch((e) => console.error('Failed to refresh token', e));
    };
    keycloak.onAuthRefreshSuccess = () => setAuthenticated(true);
    keycloak.onAuthRefreshError = () => {
      setAuthenticated(false);
      posthog.capture('user_token_refresh_failed');
    };

    // Normalize pathname by stripping trailing slash so /register/ matches /register
    const normalizedPath =
      typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';
    // Exact match for /register (Keycloak redirect page) — must not match /register-as
    const isRegisterPage = normalizedPath === '/register';
    // Custom Agent Studio signup pages under /register-as/*
    const isRegisterAsPage = normalizedPath.startsWith('/register-as');

    const defaultCreateLoginUrl = keycloak.createLoginUrl.bind(
      keycloak,
    ) as Keycloak['createLoginUrl'];

    keycloak.createLoginUrl = (options) => {
      const themeMode = getThemeMode();
      const state = new URLSearchParams(window.location.search).get('s');
      return defaultCreateLoginUrl(options).then((url) => {
        return `${url}&theme=${themeMode}${state ? `&s=${state}` : ''}`;
      });
    };

    const isAnyRegisterPage = isRegisterPage || isRegisterAsPage;

    const initOptions = {
      onLoad: 'check-sso',
      // For non-register pages, use a hidden iframe to check for an existing
      // Keycloak session without a full page redirect. If a session exists,
      // tokens are obtained silently; if not, onReady triggers keycloak.login().
      ...(typeof window !== 'undefined' &&
        !isAnyRegisterPage && {
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        }),
      enableLogging: process.env.GATSBY_ENV === 'dev',
      checkLoginIframe: false,
      pkceMethod: 'S256',
      adapter: 'default',
      responseMode: 'fragment',
    } as KeycloakInitOptions;

    keycloak.init(initOptions).catch((e: Error) => {
      console.error('[AUTH INIT] Failed to initialize Keycloak:', e?.message || 'Unknown error');
      // If silent SSO iframe failed (e.g., blocked by browser), fall back to login redirect
      if (!isAnyRegisterPage) {
        void keycloak.login();
      }
    });

    setAuthClient(keycloak);
  }, [keycloakLoading, keycloak]);

  useEffect(() => {
    if (!authenticated || !refreshToken) return () => {};

    const refreshInterval = setInterval(
      () => {
        void refreshToken(false);
      },
      60 * 60 * 1000,
    );

    return () => clearInterval(refreshInterval);
  }, [authenticated, refreshToken]);

  const value = useMemo(
    () => ({
      loading,
      error,
      user,
      authenticated,
      token: authClient?.token,
      logout,
      register,
    }),
    [loading, user, error, authenticated, authClient?.token, logout, register],
  );

  // Normalize pathname by stripping trailing slash so /register/ matches /register
  const renderPath =
    typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';
  // Exact match for /register — must not match /register-as
  const isRegisterPage = renderPath === '/register';
  // Custom Agent Studio signup pages under /register-as/*
  const isRegisterAsPage = renderPath.startsWith('/register-as');

  // Both register flows skip the transition screen so their own UI can render
  const isAnyRegisterPage = isRegisterPage || isRegisterAsPage;
  const shouldShowTransition =
    (loading && !isAnyRegisterPage) || (!authenticated && !isAnyRegisterPage);

  if (shouldShowTransition) {
    return <TransitionScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
